import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from "vue-router";

import HomeView from "@/views/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import NewSessionView from "@/views/NewSessionView.vue";
import TunnelsView from "@/views/TunnelsView.vue";
import ChatView from "@/views/ChatView.vue";
import WorkersView from "@/views/WorkersView.vue";
import TerminalView from "@/views/TerminalView.vue";
import SplitView from "@/views/SplitView.vue";
import RelayConsoleView from "@/views/RelayConsoleView.vue";
import NxEndpointView from "@/views/NxEndpointView.vue";
import AgentDetailView from "@/views/AgentDetailView.vue";
import AgentShellView from "@/views/AgentShellView.vue";
import SettingsView from "@/views/SettingsView.vue";
import PlaceholderView from "@/views/PlaceholderView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import AdminLayout from "@/views/admin/AdminLayout.vue";
import OrganizationsView from "@/views/admin/OrganizationsView.vue";
import OperatorsView from "@/views/admin/OperatorsView.vue";
import AgentsView from "@/views/admin/AgentsView.vue";
import NxEndpointsView from "@/views/admin/NxEndpointsView.vue";
import ReleasesView from "@/views/admin/ReleasesView.vue";
import AuditView from "@/views/admin/AuditView.vue";
import BetaRequestsView from "@/views/admin/BetaRequestsView.vue";
import { useConfigStore } from "@/stores/config";
import { useAuthStore } from "@/stores/auth";
import type { DeploymentMode, OperatorRole } from "@/api/types";

declare module "vue-router" {
  // Make `meta.modes`, `meta.titleKey`, `meta.requiresAuth`, and
  // `meta.requiresRole` type-checked across the codebase.
  interface RouteMeta {
    titleKey?: string;
    modes?: DeploymentMode[];
    /** Defaults to `true` for everything except routes that opt out. */
    requiresAuth?: boolean;
    /** Restrict to operators with one of these roles. */
    requiresRole?: OperatorRole[];
  }
}

// Routes from §7.2. `meta.modes` restricts a route to specific deployment
// modes; the navigation guard below redirects to the 404 view when a
// restricted route is opened in a non-matching mode (see §23.2).
const routes: RouteRecordRaw[] = [
  { path: "/", name: "home", component: HomeView, meta: { titleKey: "nav.dashboard" } },
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { titleKey: "nav.login", requiresAuth: false },
  },
  { path: "/new", name: "new-session", component: NewSessionView, meta: { titleKey: "nav.new" } },
  { path: "/tunnels", name: "tunnels", component: TunnelsView, meta: { titleKey: "nav.tunnels" } },
  { path: "/chat", name: "chat", component: ChatView, meta: { titleKey: "nav.chat" } },
  { path: "/workers", name: "workers", component: WorkersView, meta: { titleKey: "nav.workers" } },
  { path: "/split", name: "split", component: SplitView, meta: { titleKey: "nav.split" } },
  {
    path: "/port-console",
    name: "port-console",
    component: RelayConsoleView,
    meta: { titleKey: "nav.port_console" },
  },
  {
    path: "/session/:sessionId",
    name: "session",
    component: TerminalView,
    meta: { titleKey: "nav.dashboard" },
  },
  {
    path: "/agents/:agentId",
    name: "agent",
    component: AgentDetailView,
    meta: { titleKey: "nav.dashboard" },
  },
  {
    path: "/agents/:agentId/shell",
    name: "agent-shell",
    component: AgentShellView,
    meta: { titleKey: "nav.dashboard" },
  },
  {
    // Admin is its own area: AdminLayout renders a persistent section rail
    // (Organizations…Audit) around the active sub-view, so switching sections
    // never loses navigation. requiresRole on the parent applies to children
    // (router merges matched meta).
    path: "/admin",
    component: AdminLayout,
    meta: { titleKey: "nav.admin", requiresRole: ["owner", "admin"] },
    children: [
      { path: "", redirect: "/admin/agents" },
      {
        path: "organizations",
        name: "admin-organizations",
        component: OrganizationsView,
        meta: { titleKey: "nav.admin" },
      },
      {
        path: "operators",
        name: "admin-operators",
        component: OperatorsView,
        meta: { titleKey: "nav.admin" },
      },
      {
        path: "agents",
        name: "admin-agents",
        component: AgentsView,
        meta: { titleKey: "nav.admin" },
      },
      {
        path: "nx-endpoints",
        name: "admin-nx-endpoints",
        component: NxEndpointsView,
        meta: { titleKey: "nav.admin", modes: ["self-hosted"] },
      },
      {
        path: "releases",
        name: "admin-releases",
        component: ReleasesView,
        meta: { titleKey: "nav.admin" },
      },
      {
        path: "audit",
        name: "admin-audit",
        component: AuditView,
        meta: { titleKey: "nav.admin" },
      },
      {
        path: "beta-requests",
        name: "admin-beta-requests",
        component: BetaRequestsView,
        meta: { titleKey: "nav.admin" },
      },
    ],
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsView,
    meta: { titleKey: "nav.settings" },
  },
  {
    path: "/settings/:section",
    name: "settings-section",
    component: SettingsView,
    meta: { titleKey: "nav.settings" },
  },

  // self-hosted-only routes (M4 wires the real view; section param is
  // preserved for deep-links from the tabs).
  {
    path: "/nx/:endpointId",
    name: "nx",
    component: NxEndpointView,
    meta: { titleKey: "nav.nx", modes: ["self-hosted"] },
  },
  {
    path: "/nx/:endpointId/:section",
    name: "nx-section",
    component: NxEndpointView,
    meta: { titleKey: "nav.nx", modes: ["self-hosted"] },
  },

  // saas-only routes
  {
    path: "/signup",
    name: "signup",
    component: PlaceholderView,
    meta: { titleKey: "nav.signup", modes: ["saas"], requiresAuth: false },
  },
  {
    path: "/signup/verify/:token",
    name: "signup-verify",
    component: PlaceholderView,
    meta: { titleKey: "nav.signup", modes: ["saas"], requiresAuth: false },
  },
  {
    path: "/account",
    name: "account",
    component: PlaceholderView,
    meta: { titleKey: "nav.account", modes: ["saas"] },
  },
  {
    path: "/account/:section",
    name: "account-section",
    component: PlaceholderView,
    meta: { titleKey: "nav.account", modes: ["saas"] },
  },
  {
    path: "/billing",
    name: "billing",
    component: PlaceholderView,
    meta: { titleKey: "nav.billing", modes: ["saas"] },
  },
  {
    path: "/billing/:section",
    name: "billing-section",
    component: PlaceholderView,
    meta: { titleKey: "nav.billing", modes: ["saas"] },
  },
  {
    path: "/status",
    name: "status",
    component: PlaceholderView,
    meta: { titleKey: "nav.status", modes: ["saas"] },
  },
  {
    path: "/owner",
    name: "owner",
    component: PlaceholderView,
    meta: { titleKey: "nav.owner", modes: ["saas"], requiresRole: ["owner"] },
  },

  { path: "/:pathMatch(.*)*", name: "not-found", component: NotFoundView },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Combined navigation guard (§23.2 mode gating + §9.1 auth + §8 role).
router.beforeEach(async (to) => {
  // 1. Deployment mode gate. Hides saas-only routes in self-hosted and
  //    vice versa via the not-found view.
  if (to.meta.modes && to.meta.modes.length > 0) {
    try {
      const config = await useConfigStore().ensureLoaded();
      if (!to.meta.modes.includes(config.mode)) {
        return notFoundRedirect(to);
      }
    } catch {
      // Bootstrap error — let App.vue's error shell handle it.
      return true;
    }
  }

  // 2. Auth gate — `requiresAuth` defaults to true. Routes that opt
  //    out (login, signup) skip this entirely.
  if (to.meta.requiresAuth !== false) {
    const auth = useAuthStore();
    try {
      await auth.probe();
    } catch {
      // Probe surfaces an error state via the store. App.vue offers a retry
      // without mounting the protected view or losing the requested URL.
    }
    if (auth.state.kind === "anonymous") {
      return {
        name: "login",
        query: { redirect: to.fullPath },
      };
    }
    if (!auth.isAuthenticated()) return true;
  }

  // 3. Role gate. We've already confirmed the user is authenticated.
  if (to.meta.requiresRole && to.meta.requiresRole.length > 0) {
    const auth = useAuthStore();
    if (!auth.hasRole(to.meta.requiresRole)) {
      return notFoundRedirect(to);
    }
  }

  return true;
});

function notFoundRedirect(to: RouteLocationNormalized) {
  return {
    name: "not-found" as const,
    params: { pathMatch: to.path.replace(/^\//, "").split("/") },
    query: to.query,
  };
}
